/**
 * The landing screen.
 *
 * Its job is to make the promise ("which way, and how far?") and get out of the way. The
 * privacy note lives here, and its brevity is the point (`ProductOverview.md` §4.10).
 *
 * It used to be four buttons in a column, three of which were the same act — choose a point on a
 * map — differing only in what happened to the point afterwards. Nobody standing in a car park
 * thinks "I need a guided-arrival link"; they think "I need to tell him where the car is". So it
 * asks the only question a person actually arrives with — **somebody, or somewhere?** — and the
 * fate of a place is decided after the place exists, not before.
 */

import { describeAge } from '../lib/destination';
import { el } from '../lib/dom';
import { navigate } from '../lib/navigate';
import { listPins } from '../lib/pins';
import type { View } from '../lib/router';
import { withSimParams } from '../sim/peer';
import { markLogo } from '../ui/mark';

/** How many saved places to put on the front page before it becomes a list of its own. */
const SHOWN = 3;

export const landingView: View = (container) => {
  const pins = listPins();

  container.append(
    el(
      'main',
      { class: 'landing' },
      el(
        'header',
        { class: 'landing__head' },
        markLogo(),
        el('h1', { class: 'landing__title', text: 'Beeline' }),
        el('p', {
          class: 'landing__tagline',
          text: 'Which way, and how far. Nothing else.',
        }),
      ),

      el(
        'section',
        { class: 'landing__group' },
        el('h2', { class: 'landing__group-title', text: 'Meet somebody' }),
        el(
          'div',
          { class: 'landing__actions' },
          el(
            'button',
            {
              class: 'button button--primary',
              type: 'button',
              'data-action': 'create',
              onclick: () => navigate(withSimParams('/new')),
            },
            'Find each other',
          ),
          el(
            'button',
            {
              class: 'button button--ghost',
              type: 'button',
              'data-action': 'group',
              onclick: () => navigate(withSimParams('/group')),
            },
            'Meet as a group',
          ),
        ),
      ),

      el(
        'section',
        { class: 'landing__group' },
        el('h2', { class: 'landing__group-title', text: 'Go somewhere' }),

        // A place already saved is almost always what somebody opened the app for, so the list
        // comes before the button that makes another one.
        pins.length > 0 &&
          el(
            'ul',
            { class: 'places places--compact' },
            ...pins.slice(0, SHOWN).map((pin) => {
              const open = el(
                'button',
                { class: 'places__open', type: 'button', 'data-action': 'pin' },
                el(
                  'span',
                  { class: 'places__text' },
                  el('span', { class: 'places__label', text: pin.label }),
                  el('span', { class: 'places__age', text: describeAge(pin.pinnedAt) }),
                ),
              );
              open.addEventListener('click', () => navigate(`/pin/${pin.id}`));
              return el('li', { class: 'places__row' }, open);
            }),
          ),

        el(
          'div',
          { class: 'landing__actions' },
          el(
            'button',
            {
              class: 'button button--ghost',
              type: 'button',
              'data-action': 'place',
              onclick: () => navigate('/place'),
            },
            'Save or send a place',
          ),
          // Always offered once there is anything to manage, not only when the list overflows:
          // forgetting a place lives there, and a place you cannot forget is a place you are
          // stuck with.
          pins.length > 0 &&
            el(
              'button',
              {
                class: 'button button--ghost',
                type: 'button',
                'data-action': 'places',
                onclick: () => navigate('/pin'),
              },
              pins.length > SHOWN ? `All ${pins.length} places` : 'Your places',
            ),
        ),
      ),

      el(
        'section',
        { class: 'landing__how' },
        el('h2', { class: 'landing__how-title', text: 'How it works' }),
        el(
          'ol',
          { class: 'steps' },
          el('li', { class: 'steps__item' }, 'Send a link — no app, no account.'),
          el('li', { class: 'steps__item' }, 'Both of you allow location and compass.'),
          el('li', { class: 'steps__item' }, 'Follow the arrow until you can wave.'),
        ),
      ),

      el(
        'footer',
        { class: 'landing__footer' },
        // One paragraph, and its brevity is the point (`ProductOverview.md` §4.10).
        el('p', {
          class: 'privacy',
          text:
            'Your location is relayed straight to the person you are meeting and is never ' +
            'written down. Sessions last an hour, then everything about them is deleted. ' +
            'Saved places and arrival links never leave your phone at all. No accounts, no ' +
            'history, no tracking. We keep anonymous counts — how long, how close, how good ' +
            'the signal was — and nothing that says who or where.',
        }),
        el('p', {
          class: 'landing__limits',
          text: 'Best in the open: beaches, festivals, parks, car parks. Not for street navigation or indoors.',
        }),
      ),
    ),
  );
};
